// Utilitaires pour les optimisations de performance

// Observer pour détecter les éléments visibles
export const createIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {}
) => {
  const defaultOptions = {
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };

  return new IntersectionObserver(callback, defaultOptions);
};

// Debounce pour les événements fréquents
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

// Throttle pour les événements de scroll
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Détection de la qualité de connexion
export const getConnectionQuality = (): 'slow' | 'medium' | 'fast' => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection) {
      const effectiveType = connection.effectiveType;
      if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'slow';
      if (effectiveType === '3g') return 'medium';
      return 'fast';
    }
  }
  return 'medium'; // Default fallback
};

// Préchargement intelligent des images critiques uniquement
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Ne précharger que si l'image est critique et la connexion est bonne
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') {
        resolve(); // Skip preload on slow connections
        return;
      }
    }

    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

// Préchargement d'images avec format optimisé
export const preloadOptimizedImage = (src: string, format: 'webp' | 'avif' = 'webp'): Promise<void> => {
  if (!src.includes('supabase.co/storage')) {
    return preloadImage(src);
  }

  try {
    const url = new URL(src);
    url.searchParams.set('format', format);
    url.searchParams.set('quality', '75');
    return preloadImage(url.toString());
  } catch (e) {
    return preloadImage(src);
  }
};

// Optimisation des polices avec font-display
export const loadFont = (fontFamily: string, src: string, display: FontDisplay = 'swap') => {
  const font = new FontFace(fontFamily, `url(${src})`, { display });
  return font.load().then(loadedFont => {
    document.fonts.add(loadedFont);
    return loadedFont;
  });
};

// Gestion intelligente du lazy loading
export const shouldLazyLoad = (): boolean => {
  // Ne pas faire de lazy loading si on économise les données
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection?.saveData) return false;
  }
  
  // Pas de lazy loading sur les connexions très lentes
  return getConnectionQuality() !== 'slow';
};

// Mesure des performances
export const measurePerformance = (name: string, fn: () => void) => {
  const start = performance.now();
  fn();
  const end = performance.now();
  console.log(`${name} took ${end - start} milliseconds`);
};

// Cache simple pour les ressources
class ResourceCache {
  private cache = new Map<string, any>();

  set(key: string, value: any, ttl: number = 300000) { // 5 minutes par défaut
    const expiry = Date.now() + ttl;
    this.cache.set(key, { value, expiry });
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.value;
  }

  clear() {
    this.cache.clear();
  }
}

export const resourceCache = new ResourceCache();